<template>
    <div id="daily-summary-dashboard">
        <div class="header-block d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
                <div class="page-title">Resumo Diário</div>
                <div class="date-range-info" v-if="filters.date">{{ formattedDate }}</div>
            </div>

            <b-button class="btn-filters" variant="outline-primary" size="sm" @click="filtersOpen = true">
                <feather-icon icon="FilterIcon" class="mr-50" />
                Filtros
            </b-button>
        </div>

        <div class="body-block">
            <!-- KPIs -->
            <div class="kpi-grid mb-2">
                <b-card class="kpi-card text-center">
                    <div class="kpi-title">Total de Ordens</div>
                    <div class="kpi-value-large">{{ totalOrders }}</div>
                </b-card>
                <b-card class="kpi-card text-center">
                    <div class="kpi-title">Quantidade Total</div>
                    <div class="kpi-value-large">{{ totalQuantity }}</div>
                </b-card>
                <b-card class="kpi-card text-center">
                    <div class="kpi-title">Tempo Total (h)</div>
                    <div class="kpi-value-large">{{ totalTime }}</div>
                </b-card>
            </div>

            <!-- Table Section -->
            <b-row class="chart-row">
                <b-col cols="12" class="mb-1">
                    <b-card class="chart-card h-100">
                        <div class="d-flex justify-content-between align-items-center mb-1">
                            <div class="chart-header mb-0">Detalhamento de Ordens</div>
                            <b-button variant="brown" size="sm" @click="exportToCSV">
                                <feather-icon icon="DownloadIcon" class="mr-50" />
                                Exportar CSV
                            </b-button>
                        </div>

                        <b-table responsive :items="filteredOrders" :fields="tableFields" :busy="loading" show-empty
                            empty-text="Nenhuma ordem encontrada" striped hover small head-variant="light"
                            :per-page="perPage" :current-page="currentPage">
                            
                            <template #cell(status)="data">
                                <b-badge :style="`background-color: ${data.item.statusColor}; color: #fff;`">
                                    {{ data.value }}
                                </b-badge>
                            </template>

                            <template #table-busy>
                                <div class="text-center my-2">
                                    <b-spinner class="align-middle"></b-spinner>
                                    <strong>Carregando...</strong>
                                </div>
                            </template>
                        </b-table>

                        <div class="d-flex justify-content-end mt-1">
                            <b-pagination v-model="currentPage" :total-rows="filteredOrders.length" :per-page="perPage"
                                align="right" size="sm" class="my-0" />
                        </div>
                    </b-card>
                </b-col>
            </b-row>
        </div>

        <!-- Sidebar: Filters -->
        <b-sidebar right shadow bg-variant="white" width="320px" v-model="filtersOpen" backdrop>
            <template #header>
                <div class="d-flex justify-content-between align-items-center w-100 px-1 py-1">
                    <span class="font-weight-bold">Filtros</span>
                    <span style="cursor: pointer" @click="filtersOpen = false">
                        <feather-icon icon="XIcon" size="18" />
                    </span>
                </div>
            </template>

            <div class="filters-card">
                <div class="filter">
                    <label class="filter-label">Data:</label>
                    <input type="date" v-model="filters.date" class="date-input-sidebar" />
                </div>
                <div class="filter">
                    <label class="filter-label">Grupo</label>
                    <b-form-select v-model="filters.groupId" :options="groupOptions" size="sm" @change="onGroupChange" />
                </div>
                <div class="filter">
                    <label class="filter-label">Centro de Trabalho</label>
                    <b-form-select v-model="filters.workcenterId" :options="workcenterOptions" size="sm"
                        :disabled="!filters.groupId" @change="onWorkcenterChange" />
                </div>
                <div class="filter">
                    <label class="filter-label">Recurso</label>
                    <b-form-select v-model="filters.resourceId" :options="resourceOptions" size="sm"
                        :disabled="!filters.workcenterId" />
                </div>
            </div>

            <template #footer>
                <div class="d-flex justify-content-end px-1 pb-1">
                    <b-button size="sm" class="mr-50" variant="outline-secondary"
                        @click="resetFilters">Limpar</b-button>
                    <b-button size="sm" variant="primary" @click="applyFilters">Aplicar</b-button>
                </div>
            </template>
        </b-sidebar>
    </div>
</template>

<script>
import moment from 'moment'
import { BCard, BRow, BCol, BButton, BTable, BSpinner, BSidebar, BFormGroup, BFormSelect, BPagination, BBadge } from 'bootstrap-vue'
import jwtDefaultConfig from "@core/auth/jwt/jwtDefaultConfig"
import FeatherIcon from "@core/components/feather-icon/FeatherIcon.vue";
import { mapMutations } from "vuex";

export default {
    name: 'DailySummaryDashboard',
    components: {
        BCard, BRow, BCol, BButton, BTable, BSpinner, BSidebar, BFormGroup, BFormSelect, BPagination, BBadge,
        FeatherIcon
    },
    data() {
        return {
            loading: false,
            filtersOpen: false,
            currentSite: this.$cookies.get("userInfo").currentSite,
            
            // Data Sources
            allOrders: [],
            groupList: [],
            workcenterList: [],
            resourceList: [],

            // Filters
            filters: {
                date: moment().format('YYYY-MM-DD'),
                groupId: null,
                workcenterId: null,
                resourceId: null
            },

            perPage: 10,
            currentPage: 1,

            tableFields: [
                { key: 'orderNumber', label: 'Ordem', sortable: true },
                { key: 'operationName', label: 'Operação', sortable: true },
                { key: 'product', label: 'Produto', sortable: true },
                { key: 'resourceNames', label: 'Recurso', sortable: true },
                { key: 'statusName', label: 'Status', sortable: true },
                { key: 'producedQuantity', label: 'Qtd Produzida', sortable: true, class: 'text-right' },
                { key: 'durationHours', label: 'Duração (h)', sortable: true, class: 'text-right' }
            ],
        }
    },
    computed: {
        formattedDate() {
            return moment(this.filters.date).format('DD/MM/YYYY');
        },

        groupOptions() {
            return [
                { value: null, text: 'Todos' },
                ...this.groupList.map(g => ({ value: g.id, text: g.name }))
            ];
        },

        workcenterOptions() {
            const wcs = this.filters.groupId
                ? this.workcenterList.filter(w => w.groupId === this.filters.groupId)
                : [];
            return [
                { value: null, text: 'Todos' },
                ...wcs.map(w => ({ value: w.id, text: w.name }))
            ];
        },

        resourceOptions() {
            if (!this.filters.workcenterId) return [{ value: null, text: 'Todos' }];
            const list = this.resourceList.filter(r => r.workcenterId === this.filters.workcenterId);
            return [
                { value: null, text: 'Todos' },
                ...list.map(r => ({ value: r.id, text: r.name }))
            ];
        },

        filteredOrders() {
            let items = [...this.allOrders];

            if (this.filters.resourceId !== null) {
                items = items.filter(o => {
                    if (o.resourceIds && Array.isArray(o.resourceIds)) {
                        return o.resourceIds.includes(this.filters.resourceId);
                    }
                    return false;
                });
            } else if (this.filters.workcenterId) {
                const validResourceIds = this.resourceList
                    .filter(r => r.workcenterId === this.filters.workcenterId)
                    .map(r => r.id);
                items = items.filter(o => {
                    if (o.resourceIds && Array.isArray(o.resourceIds)) {
                        return o.resourceIds.some(id => validResourceIds.includes(id));
                    }
                    return false;
                });
            } else if (this.filters.groupId) {
                const validWorkcenterIds = this.workcenterList
                    .filter(w => w.groupId === this.filters.groupId)
                    .map(w => w.id);
                const validResourceIds = this.resourceList
                    .filter(r => validWorkcenterIds.includes(r.workcenterId))
                    .map(r => r.id);
                items = items.filter(o => {
                    if (o.resourceIds && Array.isArray(o.resourceIds)) {
                        return o.resourceIds.some(id => validResourceIds.includes(id));
                    }
                    return false;
                });
            }

            return items;
        },
        totalOrders() {
            return this.filteredOrders.length;
        },
        totalQuantity() {
            return this.filteredOrders.reduce((sum, item) => sum + (item.producedQuantity || 0), 0);
        },
        totalTime() {
             return this.filteredOrders.reduce((sum, item) => sum + (parseFloat(item.durationHours) || 0), 0).toFixed(2);
        }
    },
    mounted() {
        this.fetchInitialData();
        this.fetchOrders();
    },
    methods: {
        ...mapMutations("app", ["UPDATE_FLAG_SHOW_OVERLAY"]),
        
        async fetchInitialData() {
            try {
                // Fetch Groups
                const groupsRes = await this.$http2.get(`/api/mysfc/operator/groups/${this.currentSite}`);
                this.groupList = groupsRes.data.data;

                // Fetch Workcenters
                 const workcenterPromises = this.groupList.map(g => 
                    this.$http2.get(`/api/mysfc/operator/workcenters/${g.id}`).then(res => {
                        const wcs = res.data.data || [];
                         return wcs.map(w => ({ ...w, groupId: g.id }));
                    })
                );
                const workcentersArrays = await Promise.all(workcenterPromises);
                this.workcenterList = workcentersArrays.flat();

                // Fetch Resources
                // We need all resources to filter properly.
                const resourcePromises = this.workcenterList.map(wc => 
                    this.$http2.get(`/api/mysfc/operator/resources/${wc.id}`).then(res => {
                        const resources = res.data || [];
                        return resources.map(r => ({
                            ...r,
                            id: r.resourceId, // Mapping resourceId to id for v-select
                            name: r.resourceName,
                            workcenterId: wc.id
                        }));
                    })
                );
                const resourcesArrays = await Promise.all(resourcePromises);
                this.resourceList = resourcesArrays.flat();

            } catch (error) {
                console.error("Error fetching initial data", error);
            }
        },

        async fetchOrders() {
            this.loading = true;
            this.UPDATE_FLAG_SHOW_OVERLAY(true);
            try {
                // Using the operation endpoint from OrderList.vue
                // Assuming pageSize 1000 is enough for a day or we implement paging.
                // For a dashboard, users usually want to see all or top N. 
                // Let's try fetching a large page.
                const pageSize = 5000; 
                const startDate = this.filters.date;
                const endDate = this.filters.date;
                
                const res = await this.$http2.get(
                    `/api/mysfc/operation?startDate=${startDate}&endDate=${endDate}&allPeriod=false&siteId=${this.currentSite}&pageSize=${pageSize}&pageNumber=1`
                );

                const operations = res.data.data.operations || [];

                this.allOrders = operations.map(op => {
                    const duration = Number(op.actualDurationInSeconds || 0) / 3600;

                    // Extract resource IDs if available in op.resources or similar
                    // The API response in OrderList uses `resources` as array of strings (names).
                    // We need to map names to IDs if we want to filter by ID.
                    // Or we fetch resources from `op.resources` (if it contains objects).
                    // Based on OrderList: `this.handleFormatListToFormattedString(params.data.resources || [])`
                    // It seems `resources` is array of strings.
                    // This is tricky for filtering by ID.
                    // Let's try to match by name if ID is missing.
                    
                    // Workaround: Map resource names to IDs using our fetched `resourceList`.
                    let resourceIds = [];
                    if (op.resources && Array.isArray(op.resources)) {
                        resourceIds = op.resources.map(name => {
                            const found = this.resourceList.find(r => r.name === name);
                            return found ? found.id : null;
                        }).filter(id => id);
                    }

                    return {
                        ...op,
                        durationHours: duration.toFixed(2),
                        resourceNames: op.resources ? op.resources.join(', ') : '',
                        resourceIds: resourceIds
                    };
                });

            } catch (error) {
                console.error("Error fetching orders", error);
                this.$bvToast.toast('Erro ao buscar ordens', {
                    title: 'Erro',
                    variant: 'danger',
                    solid: true
                });
            } finally {
                this.loading = false;
                this.UPDATE_FLAG_SHOW_OVERLAY(false);
                this.filtersOpen = false;
            }
        },

        onGroupChange() {
            this.filters.workcenterId = null;
            this.filters.resourceId = null;
        },

        onWorkcenterChange() {
            this.filters.resourceId = null;
        },

        applyFilters() {
            this.fetchOrders();
        },

        resetFilters() {
            this.filters = {
                date: moment().format('YYYY-MM-DD'),
                groupId: null,
                workcenterId: null,
                resourceId: null
            };
            this.fetchOrders();
        },

        exportToCSV() {
            const items = this.filteredOrders;
            if (items.length === 0) return;

            const headers = ['Ordem', 'Operação', 'Produto', 'Recurso', 'Status', 'Qtd Produzida', 'Duração (h)'];
            const csvContent = [
                headers.join(','),
                ...items.map(item => [
                    item.orderNumber,
                    `"${item.operationName}"`,
                    `"${item.product}"`,
                    `"${item.resourceNames}"`,
                    item.statusName,
                    item.producedQuantity,
                    item.durationHours
                ].join(','))
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            link.setAttribute('href', url);
            link.setAttribute('download', `daily_summary_${moment().format('YYYYMMDD')}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }
}
</script>

<style lang="scss">
.b-sidebar-footer {
    .btn-primary {
        background-color: #974900 !important;
        border-color: #974900 !important;

        &:hover {
            background-color: #7d3b00 !important;
            border-color: #7d3b00 !important;
        }
    }
}
</style>

<style lang="scss">
#daily-summary-dashboard {
    .btn-filters {
        border-color: #974900 !important;
        color: #974900 !important;

        &:hover {
            background-color: #974900 !important;
            color: #fff !important;
        }
    }
    .header-block {
        padding: 10px 14px;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .page-title {
        font-size: 18px;
        font-weight: 700;
        color: #4c4541;
    }

    .date-range-info {
        font-size: 14px;
        color: #6c757d;
        margin-left: 12px;
        font-weight: 500;
    }

    .body-block {
        padding: 4px 14px 20px 14px;
    }

    .kpi-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 1.5rem;
        margin-bottom: 1.5rem;
    }

    .kpi-card {
        height: 100%;
        border: none;
        box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
        margin-bottom: 0;
    }

    .kpi-title {
        font-size: 14px;
        font-weight: 600;
        color: #6e6b7b;
        margin-bottom: 0.5rem;
    }

    .kpi-value-large {
        font-size: 24px;
        font-weight: 700;
        color: #5e5873;
    }

    .chart-header {
        font-size: 16px;
        font-weight: 600;
        color: #5e5873;
    }

    .filters-card {
        background: #fff;
        border: 1px solid #e9ecef;
        border-radius: 6px;
        padding: 12px;
        margin: 10px;

        .filter+.filter {
            margin-top: 8px;
        }

        .filter-label {
            display: block;
            font-weight: 600;
            font-size: 12px;
            margin-bottom: 6px;
        }

        .date-input-sidebar {
            width: 100%;
            padding: 6px 8px;
            border: 1px solid #d8d6de;
            border-radius: 4px;
            font-size: 14px;
            color: #6c757d;
            background-color: #fff;
            cursor: pointer;
            transition: border-color 0.2s;

            &:hover {
                border-color: #974900;
            }

            &:focus {
                outline: none;
                border-color: #974900;
                box-shadow: 0 0 0 2px rgba(151, 73, 0, 0.1);
            }
        }
    }
}
</style>
